import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './authStore';

export type FrameStyle = 'solid' | 'dashed' | 'double';
export type WheelAction = 'pan' | 'zoom';

export interface ArtboardPrefs {
  artboard_frame_enabled: boolean;
  artboard_frame_style: FrameStyle;
  artboard_stroke_color: string;
  artboard_stroke_width: number;
  artboard_halo_enabled: boolean;
  artboard_halo_color: string;
  artboard_halo_width: number;
  artboard_dash_length: number;
  nav_wheel_action: WheelAction;
  nav_invert_scroll: boolean;
  nav_zoom_speed: number;
  nav_hint_seen: boolean;
}

export const ARTBOARD_PREFS_DEFAULTS: ArtboardPrefs = {
  artboard_frame_enabled: true,
  artboard_frame_style: 'solid',
  artboard_stroke_color: '#000000cc',
  artboard_stroke_width: 2.5,
  artboard_halo_enabled: true,
  artboard_halo_color: '#ffffffcc',
  artboard_halo_width: 1.5,
  artboard_dash_length: 8,
  nav_wheel_action: 'pan',
  nav_invert_scroll: false,
  nav_zoom_speed: 1.0,
  nav_hint_seen: false,
};

interface EditorPrefsState {
  prefs: ArtboardPrefs;
  loaded: boolean;
  loadingForUserId: string | null;
  saveTimer: ReturnType<typeof setTimeout> | null;

  ensureLoaded: () => Promise<void>;
  update: (patch: Partial<ArtboardPrefs>) => void;
  resetToDefaults: () => void;
}

const SAVE_DEBOUNCE_MS = 500;

async function loadFromSupabase(userId: string): Promise<ArtboardPrefs | null> {
  const { data, error } = await supabase
    .from('cs_editor_preferences')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) {
    console.warn('[EditorPrefs] load failed:', error.message);
    return null;
  }
  if (!data) return null;
  return {
    artboard_frame_enabled: !!data.artboard_frame_enabled,
    artboard_frame_style: (data.artboard_frame_style ?? 'solid') as FrameStyle,
    artboard_stroke_color: data.artboard_stroke_color ?? ARTBOARD_PREFS_DEFAULTS.artboard_stroke_color,
    artboard_stroke_width: Number(data.artboard_stroke_width ?? ARTBOARD_PREFS_DEFAULTS.artboard_stroke_width),
    artboard_halo_enabled: !!data.artboard_halo_enabled,
    artboard_halo_color: data.artboard_halo_color ?? ARTBOARD_PREFS_DEFAULTS.artboard_halo_color,
    artboard_halo_width: Number(data.artboard_halo_width ?? ARTBOARD_PREFS_DEFAULTS.artboard_halo_width),
    artboard_dash_length: Number(data.artboard_dash_length ?? ARTBOARD_PREFS_DEFAULTS.artboard_dash_length),
    nav_wheel_action: (data.nav_wheel_action ?? ARTBOARD_PREFS_DEFAULTS.nav_wheel_action) as WheelAction,
    nav_invert_scroll: data.nav_invert_scroll ?? ARTBOARD_PREFS_DEFAULTS.nav_invert_scroll,
    nav_zoom_speed: Number(data.nav_zoom_speed ?? ARTBOARD_PREFS_DEFAULTS.nav_zoom_speed),
    nav_hint_seen: data.nav_hint_seen ?? ARTBOARD_PREFS_DEFAULTS.nav_hint_seen,
  };
}

async function persist(userId: string, prefs: ArtboardPrefs) {
  const row = { user_id: userId, ...prefs, updated_at: new Date().toISOString() };
  const { error } = await supabase
    .from('cs_editor_preferences')
    .upsert(row, { onConflict: 'user_id' });
  if (error) {
    console.warn('[EditorPrefs] save failed:', error.message);
  }
}

export const useEditorPrefsStore = create<EditorPrefsState>((set, get) => ({
  prefs: { ...ARTBOARD_PREFS_DEFAULTS },
  loaded: false,
  loadingForUserId: null,
  saveTimer: null,

  ensureLoaded: async () => {
    const userId = useAuthStore.getState().user?.id ?? null;
    const state = get();
    if (!userId) {
      if (!state.loaded) set({ loaded: true });
      return;
    }
    if (state.loadingForUserId === userId) return;
    set({ loadingForUserId: userId });
    const loaded = await loadFromSupabase(userId);
    set({
      prefs: loaded ?? { ...ARTBOARD_PREFS_DEFAULTS },
      loaded: true,
    });
  },

  update: (patch) => {
    const next = { ...get().prefs, ...patch };
    set({ prefs: next });

    const userId = useAuthStore.getState().user?.id ?? null;
    if (!userId) return;
    const existing = get().saveTimer;
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      void persist(userId, get().prefs);
      set({ saveTimer: null });
    }, SAVE_DEBOUNCE_MS);
    set({ saveTimer: timer });
  },

  resetToDefaults: () => {
    get().update({ ...ARTBOARD_PREFS_DEFAULTS });
  },
}));
